import React, { createContext, useContext, useState, useEffect } from "react";

const KitchenContext = createContext({
  kitchens: [], selectedKitchen: "", selectedKitchenName: "All Kitchens",
  changeKitchen: () => {}, refreshKitchens: () => {},
  isKitchenLocked: false,   // true when user has exactly 1 kitchen (auto-selected)
});

const KITCHEN_KEY = "manna_selected_kitchen";

export function KitchenProvider({ children }) {
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState(
    localStorage.getItem(KITCHEN_KEY) || ""
  );

  // true when the user only has one kitchen — field should be locked / read-only
  const isKitchenLocked = kitchens.length === 1;

  // Derive name from selectedKitchen — "" = "All Kitchens"
  const selectedKitchenName = selectedKitchen
    ? (kitchens.find(k => k.id === selectedKitchen)?.name || "")
    : "All Kitchens";

  // changeKitchen: only allowed for multi-kitchen users
  const changeKitchen = (id) => {
    if (isKitchenLocked) return; // can't change if locked to 1 kitchen
    setSelectedKitchen(id);
    localStorage.setItem(KITCHEN_KEY, id);
  };

  // loadKitchens: called once after login with the user's available kitchens list
  const loadKitchens = (ks) => {
    if (!ks || ks.length === 0) return;
    setKitchens(ks);

    // Single-kitchen users: always auto-select and lock
    if (ks.length === 1) {
      setSelectedKitchen(ks[0].id);
      localStorage.setItem(KITCHEN_KEY, ks[0].id);
      return;
    }

    // Multi-kitchen: restore last selection if still valid
    const stored = localStorage.getItem(KITCHEN_KEY);
    if (stored === "" || stored === null) return;
    const valid = ks.find(k => k.id === stored);
    if (!valid) {
      setSelectedKitchen("");
      localStorage.setItem(KITCHEN_KEY, "");
    }
  };

  const refreshKitchens = loadKitchens;

  return (
    <KitchenContext.Provider value={{
      kitchens, selectedKitchen, selectedKitchenName,
      changeKitchen, refreshKitchens, loadKitchens, isKitchenLocked
    }}>
      {children}
    </KitchenContext.Provider>
  );
}

export function useKitchen() {
  return useContext(KitchenContext);
}

export default KitchenContext;
